import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import get from 'lodash/get';
import qs from 'qs';
import Box from '@material-ui/core/Box';

import { LOCATION_BASE, LOCATION_OFFLINE } from 'locations/constants/paths';
import { CART_ITEMS_EXPIRED } from 'cart/constants/notificationTypes';
import { closeModal } from 'base/actions/modal';
import { isMobileMedia } from 'utils/media';
import { MENU } from 'menus/constants/entities';

import ModifyFulfillmentTime from 'menus/components/ModifyFulfillmentTime';
import MenuSectionsContainer from 'menus/containers/MenuSectionsContainer';
import MenuModalManager from 'menus/containers/MenuModalManager';
import ErrorBoundary from 'base/components/ErrorBoundary';

import NotificationModalContainer from 'base/containers/NotificationModalContainer';
import { resetError } from 'cart/actions';
import { openNotificationModal } from 'base/actions/notificationModals';
import SuccessfulAddSnackbar from 'base/components/SuccessfulAddSnackbar';
import { resetLastItem } from 'cart/actions/lastItem';
import PageLoadSpinner from 'base/ui_components/PageLoadSpinner';

import LocationsCustomMessage from 'locations/components/LocationsCustomMessage';
import { getCustomMessageSelector } from 'account/reducers/index';
import { getItemByIdSelector, isSoleEntity } from 'base/reducers/bentoApi';

import { ENABLE_DELIVERY } from 'base/constants/flags';
import FeatureFlag from 'base/components/FeatureFlag';
import { isLocationClosedSelector } from 'locations/selectors';

class MenuContainer extends React.PureComponent {
  static propTypes = {
    shouldOpenOrderSettingModal: PropTypes.bool,
    shouldOpenMenuItemDetailModal: PropTypes.bool,
    shouldOpenEditCartItemModal: PropTypes.bool,
    buttonLabel: PropTypes.string,
    fulfillment_date: PropTypes.string,
    fulfillment_time: PropTypes.string,
    requested_fulfillment_is_asap: PropTypes.bool,
    currentPrepTime: PropTypes.number,
    restaurantName: PropTypes.string,
    dispatch: PropTypes.func,
    selectedLocationIsClosed: PropTypes.bool,
    location: PropTypes.object,
    lastItem: PropTypes.string,
    menuIsFetching: PropTypes.bool,
    customMessage: PropTypes.string,
    isSingleLocation: PropTypes.bool,
    shouldOpenCart: PropTypes.bool,
    scrollManager: PropTypes.object,
    error: PropTypes.string,
    googleLoaded: PropTypes.bool.isRequired,
    menuId: PropTypes.number
  };

  static defaultProps = {
    menuId: null
  };

  componentDidMount() {
    this.determineScrollPosition();
    this.handleExpiredItems();
  }

  /**
   * Only call scroll if a position > 0 exist. The scroll position of
   * the Menu is set in the menu item detail click handler. The menu scroll
   * position gets reset to 0 on all other components.
   */
  determineScrollPosition = () => {
    const { scrollManager } = this.props;
    if (isMobileMedia() && scrollManager[MENU] > 0) {
      window.scroll(0, scrollManager[MENU]);
    }
  };

  resetError = () => {
    this.props.dispatch(resetError());
  };
  handleExpiredItems = () => {
    const { location, dispatch } = this.props;
    const params = qs.parse(location.search.replace('?', ''));
    const expired = params['expired'];
    if (expired) {
      dispatch(openNotificationModal(CART_ITEMS_EXPIRED));
    }
  };

  render() {
    const {
      shouldOpenOrderSettingModal,
      shouldOpenMenuItemDetailModal,
      shouldOpenEditCartItemModal,
      buttonLabel,
      fulfillment_date,
      fulfillment_time,
      requested_fulfillment_is_asap,
      currentPrepTime,
      restaurantName,
      dispatch,
      menuId,
      lastItem,
      menuIsFetching,
      customMessage,
      selectedLocationIsClosed,
      isSingleLocation,
      shouldOpenCart,
      error,
      googleLoaded
    } = this.props;

    /**
     * Redirect to offline page if it's single location
     * and there are errors loading the menu
     */
    if (error && isSingleLocation) {
      return <Redirect to={LOCATION_OFFLINE} />;
    }

    /**
     * redirect the user to locations list page if the specified
     * location is offline
     *
     * Return to the locations list page if there are errors and
     * it is multi location
     */
    if (selectedLocationIsClosed || (error && !isSingleLocation)) {
      dispatch(closeModal());
      return <Redirect to={LOCATION_BASE} />;
    }

    if (menuIsFetching || !googleLoaded) {
      return <PageLoadSpinner />;
    }

    return (
      <ErrorBoundary>
        <React.Fragment>
          {customMessage && isSingleLocation && (
            <Box textAlign="center">
              <LocationsCustomMessage message={customMessage} />
            </Box>
          )}
          <FeatureFlag
            flag={ENABLE_DELIVERY}
            renderComponent={<div style={{ marginBottom: '10px' }} />}
            fallbackComponent={
              <div style={{ marginBottom: '3px' }}>
                <ModifyFulfillmentTime
                  buttonLabel={buttonLabel}
                  requested_fulfillment_is_asap={requested_fulfillment_is_asap}
                  fulfillment_date={fulfillment_date}
                  fulfillment_time={fulfillment_time}
                  currentPrepTime={currentPrepTime}
                  restaurantName={restaurantName}
                />
              </div>
            }
          />
          {menuId && <MenuSectionsContainer />}
          <MenuModalManager
            shouldOpenMenuItemDetailModal={shouldOpenMenuItemDetailModal}
            shouldOpenOrderSettingModal={shouldOpenOrderSettingModal}
            shouldOpenEditCartItemModal={shouldOpenEditCartItemModal}
            shouldOpenCart={shouldOpenCart}
          />

          <SuccessfulAddSnackbar
            open={!!lastItem}
            item={lastItem}
            onClose={() => this.props.dispatch(resetLastItem())}
          />
          <NotificationModalContainer />
        </React.Fragment>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = ({
  cart,
  restaurantLocations,
  lastItem,
  menu,
  account,
  scrollManager,
  libraryLoad
}) => {
  const selectedLocation = getItemByIdSelector(
    restaurantLocations,
    cart.online_ordering_location
  );

  const isClosed = isLocationClosedSelector(
    restaurantLocations,
    cart.online_ordering_location
  );

  return {
    customMessage: getCustomMessageSelector(account),
    isSingleLocation: isSoleEntity(restaurantLocations),
    selectedLocationIsClosed: isClosed,
    menuIsFetching: menu.isFetching,
    restaurantName: get(selectedLocation, 'location.name'),
    currentPrepTime: selectedLocation.current_prep_time,
    requested_fulfillment_is_asap: cart.requested_fulfillment_is_asap,
    fulfillment_date: cart.fulfillment_date,
    fulfillment_time: cart.fulfillment_time,
    lastItem: lastItem.add,
    menuId: menu.id,
    scrollManager,
    error: menu.error,
    googleLoaded: libraryLoad.google
  };
};
export default withRouter(connect(mapStateToProps)(MenuContainer));
